import React from 'react'
import { connect } from 'react-redux'
import Droppable from 'components/DragAndDrop/Droppable'
import Draggable from 'components/DragAndDrop/Draggable'
import { useDragStore } from 'components/DragAndDrop/DragContext'
import { ExpandableFormBlock, DummyFormIcon } from 'components/Forms/Forms'
import Portal from 'components/Portal/Portal'
import { ConfirmDialog } from 'components/Modal/Modal'
import CloneIcon from '@tecuity/sos-icons/react/clone'
import ExportIcon from '@tecuity/sos-icons/react/export'
import PencilIcon from '@tecuity/sos-icons/react/pencil'
import ArchiveIcon from '@tecuity/sos-icons/react/archive'
import format from 'date-fns/format'
import { toggleDragging, moveWizard, publishWizard, unpublishWizard } from './FormManagerActions'
import { noop } from 'utilities'
import { history } from '../../store'

const DraggableWizard = ({
  droppable,
  draggable,
  title,
  formDefinitionId,
  categoryId,
  next,
  description,
  icon,
  hasFilter,
  lastModifiedDate,
  lastModifiedName,
  stopScroll,
  onRequestExport,
  onRequestClone,
  onRequestArchive,
  onRequestImport,
  moveWizard,
  publishWizard
}) => {
  const { state: dragState } = useDragStore()
  const [cloneModalOpen, setCloneModalOpen] = React.useState(false)
  const [archiveModalOpen, setArchiveModalOpen] = React.useState(false)
  const [expanded, setExpanded] = React.useState(false)
  const [isDragging, setIsDragging] = React.useState(false)
  const [dragOverDirection, setDragOverDirection] = React.useState(null)
  const dummyForm = React.useRef()

  const onDragStart = () => {
    setIsDragging(true)
  }
  const onDragEnd = () => {
    setIsDragging(false)
    // stopScroll()
  }
  const handleDropAccepted = data => {
    if (data.formDefinitionId !== formDefinitionId) {
      let target =
        dragOverDirection === 'right'
          ? { categoryId, formDefinitionId: next.FORM_DEFINITION_ID }
          : { categoryId, formDefinitionId }
      if (data.categoryId !== 'uncategorized') {
        moveWizard({ origin: data, target })
      } else {
        publishWizard({ origin: data, target })
      }
    }
  }
  const editWizard = () => history.push(`edit/${formDefinitionId}`)

  const openCloneModal = () => {
    setCloneModalOpen(true)
  }

  const handleDrag = ({ x, y }) => {
    dummyForm.current.style.transform = `translate(${x - 40}px,${y - 40}px)`
  }

  const handleDragLeftEnter = () => {
    setDragOverDirection('left')
  }

  const handleDragExit = () => {
    setDragOverDirection(null)
  }

  const handleDragRightEnter = () => {
    setDragOverDirection('right')
  }

  return (
    <div
      className={`draggable-wizard form-block-wrapper`}
      data-drag-over-direction={dragOverDirection}
    >
      <Draggable
        disabled={!draggable}
        onDragStart={onDragStart}
        onDrag={handleDrag}
        onDragEnd={onDragEnd}
        type="WIZARD"
        data={{ formDefinitionId, categoryId }}
        className="full-width"
      >
        <WizardBlock
          // isActive={isActive}
          // isDragging={isDragging}
          description={description}
          formDefinitionId={formDefinitionId}
          icon={icon}
          label={title}
          onRequestClone={openCloneModal}
          onRequestArchive={e => {
            e.stopPropagation()
            setArchiveModalOpen(true)
          }}
          onRequestExport={onRequestExport}
          expanded={expanded}
          setCollapsed={() => setExpanded(false)}
          setExpanded={() => setExpanded(true)}
          lastModifiedDate={lastModifiedDate}
          lastModifiedName={lastModifiedName}
          onRequestEdit={editWizard}
        />
      </Draggable>
      {isDragging && (
        <DummyFormIcon
          innerRef={dummyForm}
          icon={icon}
          className="wizard-dragging"
          style={{
            transform: `translate(0px, 0px)`
          }}
        />
      )}
      {cloneModalOpen && (
        <Portal isOpened={cloneModalOpen}>
          <ConfirmDialog
            closeModal={() => setCloneModalOpen(false)}
            message={`Choose a title for the new wizard.`}
            buttons={[
              { label: 'Cancel', onClick: noop, className: 'btn-default' },
              { label: 'Clone', onClick: onRequestClone, submit: true }
            ]}
            inputs={[{ label: '', type: 'text', name: 'title', placeholder: 'New Wizard Title' }]}
            modalStyle={{ borderRadius: 3 }}
          />
        </Portal>
      )}
      {archiveModalOpen && (
        <Portal isOpened={archiveModalOpen}>
          <ConfirmDialog
            closeModal={() => setArchiveModalOpen(false)}
            message={`Are you sure you want to archive "${title}"?`}
            buttons={[
              { label: 'No', onClick: noop, className: 'btn-default' },
              { label: 'Yes', onClick: onRequestArchive, className: 'btn-danger' }
            ]}
            modalStyle={{ borderRadius: 3 }}
          />
        </Portal>
      )}
      {dragState.isDragging && (
        <React.Fragment>
          <Droppable
            onDragEnter={handleDragLeftEnter}
            onDragExit={handleDragExit}
            onDrop={handleDragExit}
            onDropAccepted={handleDropAccepted}
            accepts="WIZARD"
            className="drag-over-trigger"
            disabled={droppable === false}
          />
          <Droppable
            onDragEnter={handleDragRightEnter}
            onDragExit={handleDragExit}
            onDrop={handleDragExit}
            onDropAccepted={handleDropAccepted}
            accepts="WIZARD"
            className="drag-over-trigger right"
            disabled={droppable === false}
          />
        </React.Fragment>
      )}
    </div>
  )
}

export default connect(
  null,
  {
    toggleDragging,
    moveWizard,
    publishWizard,
    unpublishWizard
  }
)(DraggableWizard)

const Label = ({ tip }) => <span className="label">{tip}</span>

const WizardBlock = ({
  events,
  isActive,
  label,
  description,
  formDefinitionId,
  icon,
  isDragging,
  onRequestClone,
  onRequestArchive,
  onRequestExport,
  expanded,
  setCollapsed,
  setExpanded,
  lastModifiedDate,
  lastModifiedName,
  onRequestEdit
}) => {
  return (
    <ExpandableFormBlock
      className={`inner-block ${!isActive ? 'transition' : 'faded'}`}
      label={label}
      description={description}
      onStartCollapse={setCollapsed}
      onStartExpand={setExpanded}
      formDefinitionId={formDefinitionId}
      icon={icon}
      hideButtons={isDragging}
      collapsedChildren={
        <div className="hover-edit-wrapper" onClick={onRequestEdit}>
          <span className="hover-edit">
            <PencilIcon />
          </span>
        </div>
      }
      metadata={
        <div className="metadata-row">
          <p className="description">{`Last Modified Date: ${format(new Date(lastModifiedDate),
            'MM/dd/yyyy h:mm a'
          )}`}</p>
          {lastModifiedName ? (
            <p className="description">{`Last Modified By: ${lastModifiedName}`}</p>
          ) : null}
        </div>
      }
      actions={
        <div className="actions-row">
          <button className="btn btn-primary btn-text" onClick={onRequestArchive} alt="Delete">
            <Label tip="Delete" />
            <ArchiveIcon />
          </button>
          <button className="btn btn-primary btn-text" onClick={onRequestClone} alt="Clone">
            <Label tip="Clone" />
            <CloneIcon />
          </button>
          <button className="btn btn-primary btn-text" onClick={onRequestExport} alt="Export">
            <Label tip="Export" />
            <ExportIcon />
          </button>
          <button className="btn btn-primary btn-text" onClick={onRequestEdit} alt="Edit">
            <Label tip="Edit" />
            <PencilIcon />
          </button>
        </div>
      }
    />
  )
}



// WEBPACK FOOTER //
// ./src/components/FormManager/DraggableWizard.js